import React from 'react';
import { useTableauRemuneration } from '../hooks/useBulletins';
import { PeriodeSelector } from '../components/PeriodeSelector';
import { useBulletinsStore } from '../store/bulletins.store';
import { rapportsApi } from '../../rapports/api/rapports.api';

export function TableauEtatRemunerationPage() {
  const { filtres, setFiltres } = useBulletinsStore();
  const { data, isLoading, isError } = useTableauRemuneration(filtres.annee, filtres.mois);
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  const f = (n: number) => n.toLocaleString('fr-FR');

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await rapportsApi.exporterEtatRemuneration(filtres.annee, filtres.mois);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Erreur lors de l\'export. Vérifiez que les bulletins ont été générés pour cette période.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-content">Tableau d'état des rémunérations</h1>
        <div className="flex items-center gap-3">
          <PeriodeSelector mois={filtres.mois} annee={filtres.annee} onChange={(m, a) => setFiltres({ mois: m, annee: a })} />
          <button
            onClick={handleExport}
            disabled={isExporting || !data}
            className="inline-flex items-center gap-2 px-4 py-2 bg-success text-content-inverted text-sm font-medium rounded-lg hover:bg-success/80 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isExporting ? 'Export en cours...' : 'Exporter XLSX'}
          </button>
        </div>
      </div>

      {exportError && <p className="text-danger text-sm mb-4">{exportError}</p>}
      {isLoading && <p className="text-content-subtle text-sm">Chargement...</p>}
      {isError && <p className="text-danger text-sm">Erreur lors du chargement.</p>}

      {data && (
        <>
          <p className="text-sm text-content-muted mb-4">{data.nombreEmployes} employé(s) — {data.mois}/{data.annee}</p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-surface-raised">
                <tr>
                  {['Employé', 'Base', 'Brut', 'CNSS', 'ITS', 'Net', 'À payer'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-medium text-content-subtle uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-border">
                {data.lignes.map(l => (
                  <tr key={l.idEmploye} className="hover:bg-surface-raised">
                    <td className="px-3 py-2 font-medium text-content">{l.nomEmploye} {l.prenomEmploye}</td>
                    <td className="px-3 py-2 text-content-muted">{f(l.salaireBase)}</td>
                    <td className="px-3 py-2 text-content-muted">{f(l.salaireBrut)}</td>
                    <td className="px-3 py-2 text-danger">{f(l.montantCNSS)}</td>
                    <td className="px-3 py-2 text-danger">{f(l.montantITS)}</td>
                    <td className="px-3 py-2 text-content-muted">{f(l.salaireNet)}</td>
                    <td className="px-3 py-2 font-semibold text-success">{f(l.netAPayer)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-surface-overlay font-semibold">
                <tr>
                  <td className="px-3 py-2 text-content-muted">TOTAUX</td>
                  <td className="px-3 py-2">{f(data.totaux.salaireBase)}</td>
                  <td className="px-3 py-2">{f(data.totaux.salaireBrut)}</td>
                  <td className="px-3 py-2 text-danger">{f(data.totaux.montantCNSS)}</td>
                  <td className="px-3 py-2 text-danger">{f(data.totaux.montantITS)}</td>
                  <td className="px-3 py-2">{f(data.totaux.salaireNet)}</td>
                  <td className="px-3 py-2 text-success">{f(data.totaux.netAPayer)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
